import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { EventFormType } from '../../../../types/events.types';
import { ChildrenFormProps } from '../event-form.types';
import {
  FormSchema,
  GrantSignatureOption,
  optionalStep as optionalStepSchema,
} from '../event-form.validation';
import { GrantStepFourth } from '../step-components/grant-steps/grant-step-fourth';
import { FundraisingRoundOptionalStep, ShareIssuanceOptionalStep } from '../step-components/index';

export type OptionalStepProps = ChildrenFormProps;

export const OptionalStep: FC<OptionalStepProps> = ({
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  control,
  setFormData,
  companyId,
  lockMode,
  filedState,
  clearErrors,
  errors,
  formData,
  setError,
  isLoading: isPendingSave,
  event,
  issuedShares,
  previewMode,
  reset,
  transactionId,
  isGrantEditWizard,
}) => {
  const { initialStep, optionalStep, stepOne } = useWatch<FormSchema>({ control });
  const { success } = optionalStepSchema.safeParse(optionalStep);

  const isValid = success && !filedState('stepOne').invalid;

  const isGrantSignatureRecipientsStep =
    initialStep?.type === EventFormType.GRANT &&
    stepOne?.eventDetails?.grant?.enabled &&
    stepOne.eventDetails.grant.signatureOption === GrantSignatureOption.SIGNATURE &&
    !!stepOne.eventDetails.grant.selectedTemplateId;

  return (
    <>
      <div
        className={`invisible-y-scroll flex h-full flex-col rounded-b-xl ${isGrantEditWizard ? 'gap-4' : 'gap-6 border-b border-gray-300 bg-white'}`}
      >
        {stepOne?.eventDetails?.['fundraising-round']?.enabled && (
          <FundraisingRoundOptionalStep
            clearErrors={clearErrors}
            companyId={companyId}
            control={control}
            errors={errors}
            event={event}
            formData={formData}
            lockMode={lockMode}
            setError={setError}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['share-issuance']?.enabled && (
          <ShareIssuanceOptionalStep
            clearErrors={clearErrors}
            companyId={companyId}
            control={control}
            lockMode={lockMode}
            setFormData={setFormData}
          />
        )}
        {isGrantSignatureRecipientsStep && (
          <GrantStepFourth
            clearErrors={clearErrors}
            companyId={companyId}
            control={control}
            errors={errors}
            event={event}
            filedState={filedState}
            formData={formData}
            handleCloseModal={handleCloseModal}
            isGrantEditWizard={isGrantEditWizard}
            isLoading={isPendingSave}
            issuedShares={issuedShares}
            lockMode={lockMode}
            nextFormStep={nextFormStep}
            prevFormStep={prevFormStep}
            previewMode={previewMode}
            reset={reset}
            setError={setError}
            setFormData={setFormData}
            transactionId={transactionId}
          />
        )}
      </div>

      {!isGrantSignatureRecipientsStep && (
        <div className="flex h-9 w-full justify-between gap-3 px-6">
          <Button
            className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
            onClick={handleCloseModal}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <div className="flex gap-3">
            {(!lockMode || isGrantEditWizard) && (
              <Button
                className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
                onClick={prevFormStep}
                styleType="NONE"
                type="button"
              >
                <AppFormattedMessage id="BACK" />
              </Button>
            )}
            <Button
              className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
              disabled={!isValid}
              isLoading={isPendingSave}
              onClick={nextFormStep}
              type="button"
            >
              {lockMode && !isGrantEditWizard ? (
                <AppFormattedMessage id="UPDATE" />
              ) : (
                <AppFormattedMessage id="NEXT" />
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
